import { useEffect, useRef, useState } from "react";
import { isDemoMode } from "../firebase";
import { subscribePublishedProfile } from "../lib/profileStore";

/**
 * Live published profiles for a set of registered users, as a uid -> profile map.
 *
 * One listener per uid, added and removed as the set changes. This is what lets
 * a profile edit propagate into everyone else's rolodex without a reload.
 */
export function usePublishedProfiles(uids = []) {
  const [profiles, setProfiles] = useState(() => new Map());
  const subscriptions = useRef(new Map());
  // Sorting makes the effect dependency stable for an unchanged set.
  const key = [...new Set(uids.filter(Boolean))].sort().join("|");

  useEffect(() => {
    if (isDemoMode) return undefined;
    const wanted = new Set(key ? key.split("|") : []);
    const live = subscriptions.current;

    live.forEach((unsubscribe, uid) => {
      if (wanted.has(uid)) return;
      unsubscribe();
      live.delete(uid);
      setProfiles((previous) => {
        if (!previous.has(uid)) return previous;
        const next = new Map(previous);
        next.delete(uid);
        return next;
      });
    });

    wanted.forEach((uid) => {
      if (live.has(uid)) return;
      live.set(
        uid,
        subscribePublishedProfile(uid, (profile) => {
          setProfiles((previous) => {
            const current = previous.get(uid);
            if (current === profile) return previous;
            const next = new Map(previous);
            if (profile) next.set(uid, profile);
            else next.delete(uid);
            return next;
          });
        })
      );
    });

    return undefined;
  }, [key]);

  // Tear every listener down when the consumer unmounts.
  useEffect(() => {
    const live = subscriptions.current;
    return () => {
      live.forEach((unsubscribe) => unsubscribe());
      live.clear();
    };
  }, []);

  return profiles;
}
